import React, { useCallback, useMemo } from 'react'
import useTrans from '../../../hooks/useTrans'
import { numberNotation, priceNotation } from '../../../utils/safe'
import { useSelector } from 'react-redux'
import { Tooltip } from '../../atoms'
import { Info } from '../../atoms/NewIcons'
import { showDettagliPersonalizzaione } from '../../../utils/servicegift'

const PrezziProdottoTable = ({
  prodotto,
  selectedArticolo,
  personalizzazioni,
  prezziPersonalizzazioni,
}) => {
  const t = useTrans()
  const { service_gift } = useSelector((state) => state.servicegift)
  const { utente } = useSelector((state) => state.utente)

  const orderedFasce = useMemo(() => {
    if (!selectedArticolo?.fasce) return []
    return [...selectedArticolo.fasce].sort((a, b) => {
      return parseFloat(b.da) - parseFloat(a.da)
    })
  }, [selectedArticolo?.fasce])

  // Memoize the price lookup function with useCallback to keep its identity stable.
  const getPrezzoNeutroByQuantita = useCallback(
    (quantita) => {
      if (!selectedArticolo?.con_fasce) {
        return selectedArticolo?.prezzo
      }
      // The `orderedFasce` array is already sorted descending, so find is efficient.
      const matchingFascia = orderedFasce.find((fascia) => quantita >= fascia.da)
      return matchingFascia?.prezzo ?? null
    },
    [selectedArticolo, orderedFasce]
  )

  const prezziPersonalizzazioneConsigliata = useMemo(() => {
    const allPersonalizzazioni = prezziPersonalizzazioni?.tecniche?.flatMap((tecnica) =>
      tecnica.posizioni.flatMap((posizione) =>
        posizione.numero_colori.flatMap((numeroColore) => numeroColore.personalizzazioni_objects)
      )
    ) || []

    if (!allPersonalizzazioni?.length) {
      return []
    }

    const lavorazioneConsigliata = allPersonalizzazioni.reduce((best, current) => {
      // Find the lowest starting price for the current customization type
      const currentMinPrice = Math.min(
        ...current.prezzi_personalizzazione.map((p) => parseFloat(p.prezzo))
      )
      // Find the lowest starting price for the best-so-far customization type
      const bestMinPrice = Math.min(
        ...best.prezzi_personalizzazione.map((p) => parseFloat(p.prezzo))
      )
      return currentMinPrice < bestMinPrice ? current : best
    })

    return lavorazioneConsigliata.prezzi_personalizzazione || []
  }, [prezziPersonalizzazioni])

  const { scaglioniQuantita, neutralPrices, customizedPrices } = useMemo(() => {
    const filteredPrices =
      prezziPersonalizzazioneConsigliata?.filter((p) => p.a_partire_da != 1) || []

    // se non ci sono le personalizzazioni allora gli scaglioni sono le fasce del prezzo neutro
    const scaglioniQuantita = !!filteredPrices?.length
      ? filteredPrices.map((p) => p.a_partire_da)
      : !!orderedFasce?.length
      ? orderedFasce.map((p) => p.da).reverse()
      : [50, 100, 250, 500, 1000, 2500, 5000, 10000] // fallback nel caso non ci siano personalizzazioni o non abbia fasce

    const neutralPrices = scaglioniQuantita
      .map((quantita) => getPrezzoNeutroByQuantita(quantita) ?? null)
      .filter((p) => !!p)

    const customizedPrices = filteredPrices
      .map((p, i) => p.prezzo + neutralPrices[i])
      .filter((p) => !!p)

    return { scaglioniQuantita, neutralPrices, customizedPrices }
  }, [prezziPersonalizzazioneConsigliata, getPrezzoNeutroByQuantita])

  const gridStyle = { '--column-count': scaglioniQuantita?.length || 1 }

  return !!scaglioniQuantita?.length && !!neutralPrices?.length ? (
    <div className="prezzi-prodotto-table">
      <div className="prezzi-prodotto-table__scroll-wrapper u-scrollable-x">
        <div className="prezzi-prodotto-table__content" style={gridStyle}>
          {/* Header Row */}
          <div className="prezzi-prodotto-table__cell prezzi-prodotto-table__cell--header"></div>
          {scaglioniQuantita.map((quantita) => (
            <div
              key={quantita}
              className="prezzi-prodotto-table__cell prezzi-prodotto-table__cell--header"
            >
              {quantita}+
            </div>
          ))}

          {/* Neutral Product Prices Row */}
          <div className="prezzi-prodotto-table__cell prezzi-prodotto-table__cell--label">
            {t('Prezzo neutro')}
          </div>
          {neutralPrices.map((price, index) => (
            <div
              key={index}
              className="prezzi-prodotto-table__cell prezzi-prodotto-table__cell--price"
            >
              {priceNotation(price, 3)}€
            </div>
          ))}

          {/* Customized Product Prices Row */}
          {!!customizedPrices?.length && (
            <>
              <div className="prezzi-prodotto-table__cell prezzi-prodotto-table__cell--label">
                {t('Prezzo stampato')}
                <span className="consegna-prevista__info">
                  <Tooltip
                    label={t('Il prezzo è calcolato sulla base della lavorazione consigliata')}
                  >
                    <Info />
                  </Tooltip>
                </span>
              </div>
              {customizedPrices.map((price, index) => (
                <div
                  key={index}
                  className="prezzi-prodotto-table__cell prezzi-prodotto-table__cell--price"
                >
                  {priceNotation(price, 3)}€
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {showDettagliPersonalizzaione(prodotto, service_gift) &&
        personalizzazioni?.tecniche?.length > 0 &&
        prodotto?.quantita_minima_personalizzabile && (
          <p className={'prezzi-prodotto-table__quantita-minima'}>
            {t('Quantità minima personalizzabile:')}{' '}
            <strong>{numberNotation(prodotto?.quantita_minima_personalizzabile)}</strong>{' '}
            {t('pezzi')}
          </p>
        )}
    </div>
  ) : (
    <></>
  )
}

export default PrezziProdottoTable
